import { Ref, RefObject, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import { useClickOutside, useScrollRefEventListener } from '../../hooks';
import { Spacing } from '../../theme';
import { Dropdown, DropdownOptionProps } from '../Dropdown';
import { Icon } from '../Icon';
import { Text } from '../Text';
import {
  StyledContainer,
  StyledIconWrapper,
  StyledLabel,
  StyledLabelContainer,
} from './Select.style';

export interface SelectProps {
  id?: string;
  label?: string;
  options: Array<DropdownOptionProps>;
  onBeforeChange?: () => Promise<boolean>;
  onChange?: (selectedValue: string) => void;
  disabled?: boolean;
}

export const Select = forwardRef<HTMLDivElement, SelectProps>(SelectRefWrapper);

function SelectRefWrapper(
  { id, label, options, onChange, onBeforeChange, disabled = false }: SelectProps,
  scrollRef?: Ref<HTMLDivElement>
) {
  const selectedOption = options.find((option) => option.isSelected === true) || options[0];

  const [selected, setSelected] = useState(selectedOption);
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [dimensions, setDimensions] = useState({ top: 0, left: 0, width: 0 });
  const labelRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = () => setIsOpened(false);

  const containerRef = useClickOutside(handleClickOutside);

  const theme = useTheme();

  useEffect(() => {
    setSelected(selectedOption);
  }, [selectedOption]);

  const toggleVisibility = useCallback(
    (visible?: boolean) => {
      if (disabled) return;

      setIsOpened((isOpened: boolean) => {
        const isVisible = visible ?? !isOpened;

        if (isVisible && labelRef.current) {
          const boundingClientRect = labelRef.current.getBoundingClientRect();

          setDimensions({
            top: boundingClientRect.top + boundingClientRect.height + Spacing.Size.XXSmall,
            left: boundingClientRect.left,
            width: boundingClientRect.width,
          });
        }

        return visible ?? !isOpened;
      });
    },
    [disabled]
  );

  const handleChange = useCallback(
    (selectedValue: string, selectedIndex: number) => {
      if (disabled) return;

      setSelected((selectedState) => {
        toggleVisibility(false);
        if (selectedValue === selectedState.value) return selectedState;

        onChange?.(selectedValue);

        return { ...options[selectedIndex], isSelected: true };
      });
    },
    [onChange, options, toggleVisibility, disabled]
  );

  const onChangeHandler = useCallback(
    async (selectedValue: string, selectedIndex: number) => {
      if (onBeforeChange) {
        const shouldChange = await onBeforeChange();

        if (!shouldChange) return;

        handleChange(selectedValue, selectedIndex);
      } else {
        handleChange(selectedValue, selectedIndex);
      }
    },
    [handleChange, onBeforeChange]
  );

  const onHandleContainerScroll = () => {
    setIsOpened(false);
  };

  useScrollRefEventListener({ scrollRef, onHandleContainerScroll });

  return (
    <StyledContainer ref={containerRef as RefObject<HTMLDivElement>}>
      {label && (
        <StyledLabel htmlFor={id}>
          <Text size={theme.Form.Input.Size} showTooltip={true}>
            {label}
          </Text>
        </StyledLabel>
      )}

      <StyledLabelContainer
        onClick={() => toggleVisibility()}
        ref={labelRef}
        isOpened={isOpened}
        $disabled={disabled}
      >
        {selected.icon && (
          <Icon name={selected.icon} color={theme.Color.Neutrals.Dark} size="Small" />
        )}

        <Text size={'Body'}>{selected.label}</Text>

        <StyledIconWrapper>
          <Icon name="chevron---20" angle={isOpened ? 180 : 0} color={theme.Color.Neutrals.Dark} />
        </StyledIconWrapper>
      </StyledLabelContainer>

      <Dropdown
        options={options}
        dimensions={dimensions}
        opened={isOpened}
        onSelect={onChangeHandler}
      />
    </StyledContainer>
  );
}
